import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { RSVPTable } from "../components/admin/RSVPTable";
import { LoginModal } from "../components/auth/LoginModal";
import { getRsvps } from "../lib/api";
import type { RSVPResponse, Summary } from "../types/rsvp";

export const Route = createFileRoute("/admin")({
	component: AdminComponent,
});

function AdminComponent() {
	const [responses, setResponses] = useState<RSVPResponse[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [showLoginModal, setShowLoginModal] = useState(false);

	const [summary, setSummary] = useState<Summary>({
		total: 0,
		attending: 0,
		notAttending: 0,
	});

	useEffect(() => {
		// Kontrola autentizace
		const token = localStorage.getItem("adminToken");
		if (token) {
			setIsAuthenticated(true);
			fetchResponses(token);
		} else {
			setIsLoading(false);
			setShowLoginModal(true);
		}
	}, []);

	useEffect(() => {
		// Výpočet sumarizace při změně odpovědí
		const newSummary = responses.reduce(
			(acc, response) => {
				acc.total++;
				if (response.attending === "yes") {
					acc.attending++;
				} else {
					acc.notAttending++;
				}
				return acc;
			},
			{
				total: 0,
				attending: 0,
				notAttending: 0,
			},
		);

		setSummary(newSummary);
	}, [responses]);

	const fetchResponses = async (token: string) => {
		try {
			const result = await getRsvps();
			setResponses(result || []);
		} catch (error) {
			console.error("Chyba při načítání odpovědí:", error);
			if (error instanceof Error && error.message.includes("Neplatný token")) {
				localStorage.removeItem("adminToken");
				setIsAuthenticated(false);
				setShowLoginModal(true);
			}
		} finally {
			setIsLoading(false);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-xl text-gray-600">Načítání...</div>
			</div>
		);
	}

	if (!isAuthenticated) {
		return (
			<LoginModal
				isOpen={showLoginModal}
				onClose={() => setShowLoginModal(false)}
				onSuccess={() => {
					setIsAuthenticated(true);
					setShowLoginModal(false);
					const token = localStorage.getItem("adminToken");
					if (token) {
						fetchResponses(token);
					}
				}}
			/>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8">
			<h1 className="text-3xl font-serif text-[#7D9F7D] mb-8">Admin panel</h1>

			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
				<div className="bg-white p-6 rounded-lg shadow-md">
					<h2 className="text-xl font-serif text-gray-800 mb-2">
						Celkem odpovědí
					</h2>
					<p className="text-3xl font-bold text-[#7D9F7D]">{summary.total}</p>
				</div>
				<div className="bg-white p-6 rounded-lg shadow-md">
					<h2 className="text-xl font-serif text-gray-800 mb-2">Zúčastní se</h2>
					<p className="text-3xl font-bold text-green-600">
						{summary.attending}
					</p>
				</div>
				<div className="bg-white p-6 rounded-lg shadow-md">
					<h2 className="text-xl font-serif text-gray-800 mb-2">
						Nezúčastní se
					</h2>
					<p className="text-3xl font-bold text-red-600">
						{summary.notAttending}
					</p>
				</div>
			</div>

			<RSVPTable responses={responses} />
		</div>
	);
}
